import React, { useState, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "@/components/ui";
import { useToast } from "@finitless/shared/hooks";
import { handleApiError, handleApiSuccess } from "@finitless/shared/lib";
import { useFacebookSDK } from "@/hooks/useFacebookSDK";
import {
  useInitWhatsAppOAuth,
  useCompleteWhatsAppOAuth,
  useDisconnectWhatsAppOAuth,
  useDownloadWhatsAppPin,
  useVerifyWhatsAppRegistration,
  useDoctorWhatsApp,
} from "@/hooks/useAiAgents";
import WhatsAppDisconnectModal from "./WhatsAppDisconnectModal";

const AUTH_TYPE_MANUAL = "manual";
const AUTH_TYPE_BUSINESS_LOGIN = "business_login";

/**
 * WhatsAppAuthSection - Handles WhatsApp authentication type selection and OAuth flow
 *
 * @param {Object} props
 * @param {Object} props.agent - Full agent object with configured, misconfigured_reason, provider_settings
 * @param {string} props.authType - Current auth_type value
 * @param {Function} props.onAuthTypeChange - Callback when auth_type changes
 * @param {Function} props.onCreateAgent - Optional callback to create agent before OAuth (for create modal flow)
 * @param {boolean} props.autoConnect - If true, automatically start OAuth on mount
 * @param {boolean} props.readOnly - If true, disable all interactions
 */
const WhatsAppAuthSection = ({
  agent,
  authType = AUTH_TYPE_MANUAL,
  onAuthTypeChange,
  onCreateAgent,
  onSaveBeforeConnect,
  autoConnect = false,
  readOnly = false,
  editedPin,
}) => {
  const { t } = useTranslation("aiAgents");
  const toast = useToast();
  const { loadSDK, login, isLoading: isLoadingSDK } = useFacebookSDK();

  const initOAuthMutation = useInitWhatsAppOAuth();
  const completeOAuthMutation = useCompleteWhatsAppOAuth();
  const disconnectMutation = useDisconnectWhatsAppOAuth();
  const downloadPinMutation = useDownloadWhatsAppPin();
  const verifyRegistrationMutation = useVerifyWhatsAppRegistration();
  const doctorMutation = useDoctorWhatsApp();

  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [showAuthTypeChangeModal, setShowAuthTypeChangeModal] = useState(false);
  const [pendingAuthType, setPendingAuthType] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [showPinConfirmModal, setShowPinConfirmModal] = useState(false);

  const providerSettings = agent?.provider_settings || {};
  const isConfigured = agent?.configured === true;
  const misconfiguredReason = agent?.misconfigured_reason;
  const isBusinessLogin = authType === AUTH_TYPE_BUSINESS_LOGIN;
  const phoneNumber = providerSettings.phone_number;
  const countryCode = providerSettings.country_code;
  const connectedAt = providerSettings.connected_at;
  const pin = providerSettings.pin;
  const pinDownloadedAt = providerSettings.pin_downloaded_at;
  const registrationStatus = providerSettings.registration_status;

  // Determine connection status
  const getConnectionStatus = () => {
    if (isBusinessLogin && isConfigured) {
      return "connected";
    }
    if (isBusinessLogin && misconfiguredReason) {
      return "error";
    }
    if (isBusinessLogin && !isConfigured) {
      return "notConnected";
    }
    return null;
  };

  const connectionStatus = getConnectionStatus();

  // Handle auth type change
  const handleAuthTypeSelect = (newAuthType) => {
    if (readOnly) return;
    if (newAuthType === authType) return;

    // If changing from business_login to manual and connected, show confirmation
    if (authType === AUTH_TYPE_BUSINESS_LOGIN && isConfigured) {
      setPendingAuthType(newAuthType);
      setShowAuthTypeChangeModal(true);
      return;
    }

    onAuthTypeChange?.(newAuthType);
  };

  const handleAuthTypeChangeConfirm = () => {
    setShowAuthTypeChangeModal(false);
    if (pendingAuthType) {
      onAuthTypeChange?.(pendingAuthType);
      setPendingAuthType(null);
    }
  };

  // Handle OAuth connect
  const handleConnect = useCallback(async () => {
    let agentId = agent?.id;

    // If no agent ID and onCreateAgent is provided, create the agent first
    if (!agentId && onCreateAgent) {
      try {
        const newAgent = await onCreateAgent();
        agentId = newAgent?.id;
        if (!agentId) {
          // Creation failed or was cancelled
          return;
        }
      } catch {
        // Error already handled by onCreateAgent
        return;
      }
    }

    if (!agentId) return;

    // Save any unsaved agent changes before starting OAuth
    if (onSaveBeforeConnect) {
      const saved = await onSaveBeforeConnect();
      if (!saved) return;
    }

    setIsConnecting(true);
    try {
      // Step 1: Initialize OAuth flow
      const initResult = await initOAuthMutation.mutateAsync({
        agentId,
        authStrategy: "jssdk",
      });

      const { facebook } = initResult;
      if (!facebook?.app_id || !facebook?.config_id) {
        throw new Error("Invalid OAuth configuration received");
      }

      // Step 2: Load Facebook SDK
      await loadSDK(facebook.app_id);

      // Step 3: Open Facebook Login popup
      const loginOptions = {
        config_id: facebook.config_id,
        response_type: "code",
        override_default_response_type: true,
        scope: facebook.scope,
      };
      const response = await login(loginOptions);

      // Check if the user cancelled during the embedded signup flow
      if (response.sessionInfo?.event === "CANCEL") {
        if (response.sessionInfo?.data?.error_message) {
          toast.error(response.sessionInfo.data.error_message);
        }
        return;
      }

      // Validate session info has required data
      const sessionData = response.sessionInfo?.data;
      if (!sessionData?.waba_id) {
        toast.error(t("whatsappOAuth.toasts.missingWabaId"));
        return;
      }
      if (!sessionData?.phone_number_id) {
        toast.error(t("whatsappOAuth.toasts.missingPhoneNumberId"));
        return;
      }

      // Step 4: Send code and session info to backend to complete OAuth
      if (response.authResponse?.code) {
        const completeData = {
          agentId,
          code: response.authResponse.code,
          phone_number_id: sessionData.phone_number_id,
          waba_id: sessionData.waba_id,
        };

        await completeOAuthMutation.mutateAsync(completeData);
        handleApiSuccess(t("whatsappOAuth.toasts.connectSuccess"), toast);
      }
    } catch (error) {
      if (error.message === "Login cancelled or failed") {
        // User cancelled - no error toast needed
        return;
      }
      handleApiError(error, toast, {
        title: t("whatsappOAuth.toasts.connectError"),
        defaultMessage: t("whatsappOAuth.toasts.initError"),
      });
    } finally {
      setIsConnecting(false);
    }
  }, [
    agent?.id,
    onCreateAgent,
    onSaveBeforeConnect,
    initOAuthMutation,
    completeOAuthMutation,
    loadSDK,
    login,
    toast,
    t,
  ]);

  // Wrapper that checks for PIN before starting connect flow
  const handleConnectWithPinCheck = useCallback(() => {
    if (!editedPin) {
      setShowPinConfirmModal(true);
      return;
    }
    handleConnect();
  }, [editedPin, handleConnect]);

  // Handle PIN download
  const handleDownloadPin = useCallback(async () => {
    if (!agent?.id) return;

    try {
      await downloadPinMutation.mutateAsync({ agentId: agent.id });
      handleApiSuccess(t("whatsappOAuth.info.downloadPinSuccess"), toast);
    } catch (error) {
      handleApiError(error, toast, {
        title: t("whatsappOAuth.info.downloadPinError"),
      });
    }
  }, [agent?.id, downloadPinMutation, toast, t]);

  // Handle verify registration
  const handleVerifyRegistration = useCallback(async () => {
    if (!agent?.id) return;

    try {
      await verifyRegistrationMutation.mutateAsync({ agentId: agent.id });
      handleApiSuccess(
        t("whatsappOAuth.info.verifyRegistrationSuccess"),
        toast,
      );
    } catch (error) {
      handleApiError(error, toast, {
        title: t("whatsappOAuth.info.verifyRegistrationError"),
      });
    }
  }, [agent?.id, verifyRegistrationMutation, toast, t]);

  // Handle OAuth disconnect
  const handleDisconnect = useCallback(async () => {
    if (!agent?.id) return;

    try {
      await disconnectMutation.mutateAsync({ agentId: agent.id });
      handleApiSuccess(t("whatsappOAuth.toasts.disconnectSuccess"), toast);
      setShowDisconnectModal(false);
    } catch (error) {
      handleApiError(error, toast, {
        title: t("whatsappOAuth.toasts.disconnectError"),
      });
    }
  }, [agent?.id, disconnectMutation, toast, t]);

  // Handle doctor (repair connection)
  const handleDoctor = useCallback(async () => {
    if (!agent?.id) return;

    try {
      const response = await doctorMutation.mutateAsync({ agentId: agent.id });
      const messages = Array.isArray(response?.result)
        ? response.result
        : [response?.result || t("whatsappOAuth.toasts.doctorSuccess")];
      const isError = response?.error === true;

      messages.forEach((msg) => {
        if (isError) {
          toast.error(msg);
        } else {
          toast.success(msg);
        }
      });
    } catch (error) {
      handleApiError(error, toast, {
        title: t("whatsappOAuth.toasts.doctorError"),
      });
    }
  }, [agent?.id, doctorMutation, toast, t]);

  // Handle logout + init + complete (for account_blocked)
  const handleLogoutAndReconnect = useCallback(async () => {
    if (!agent?.id) return;

    setIsConnecting(true);
    try {
      // Step 1: Logout
      await disconnectMutation.mutateAsync({ agentId: agent.id });

      // Step 2-4: Same as handleConnect (init + SDK + complete)
      const initResult = await initOAuthMutation.mutateAsync({
        agentId: agent.id,
        authStrategy: "jssdk",
      });

      const { facebook } = initResult;
      if (!facebook?.app_id || !facebook?.config_id) {
        throw new Error("Invalid OAuth configuration received");
      }

      await loadSDK(facebook.app_id);

      const loginOptions = {
        config_id: facebook.config_id,
        response_type: "code",
        override_default_response_type: true,
        scope: facebook.scope,
      };
      const response = await login(loginOptions);

      if (response.sessionInfo?.event === "CANCEL") {
        if (response.sessionInfo?.data?.error_message) {
          toast.error(response.sessionInfo.data.error_message);
        }
        return;
      }

      const sessionData = response.sessionInfo?.data;
      if (!sessionData?.waba_id) {
        toast.error(t("whatsappOAuth.toasts.missingWabaId"));
        return;
      }
      if (!sessionData?.phone_number_id) {
        toast.error(t("whatsappOAuth.toasts.missingPhoneNumberId"));
        return;
      }

      if (response.authResponse?.code) {
        const completeData = {
          agentId: agent.id,
          code: response.authResponse.code,
          phone_number_id: sessionData.phone_number_id,
          waba_id: sessionData.waba_id,
        };

        await completeOAuthMutation.mutateAsync(completeData);
        handleApiSuccess(t("whatsappOAuth.toasts.connectSuccess"), toast);
      }
    } catch (error) {
      if (error.message === "Login cancelled or failed") return;
      handleApiError(error, toast, {
        title: t("whatsappOAuth.toasts.connectError"),
        defaultMessage: t("whatsappOAuth.toasts.initError"),
      });
    } finally {
      setIsConnecting(false);
    }
  }, [
    agent?.id,
    disconnectMutation,
    initOAuthMutation,
    completeOAuthMutation,
    loadSDK,
    login,
    toast,
    t,
  ]);

  // Get action config based on misconfigured_reason
  const getErrorAction = (reason) => {
    switch (reason) {
      case "pending_oauth":
      case "logged_out":
        return {
          handler: handleConnectWithPinCheck,
          label: t("whatsappOAuth.connectButton"),
          icon: "MessageCircle",
        };
      case "missing_access_token":
      case "invalid_access_token":
      case "revoked_by_user":
      case "auth_type_changed":
        return {
          handler: handleConnectWithPinCheck,
          label: t("whatsappOAuth.reconnectButton"),
          icon: "RefreshCw",
        };
      case "missing_phone_number_id":
        return {
          handler: handleDoctor,
          label: t("whatsappOAuth.completeSetupButton"),
          icon: "Settings",
        };
      case "missing_pin":
      case "invalid_pin":
        return {
          handler: handleDoctor,
          label: t("whatsappOAuth.diagnose.button"),
          icon: "Stethoscope",
          showDisconnect: true,
        };
      case "pin_rate_limited":
      case "phone_reverification_needed":
      case "phone_not_registered":
      case "certificate_error":
      case "registration_failed":
      case "registration_rate_limited":
        return {
          handler: handleDoctor,
          label: t("whatsappOAuth.retryButton"),
          icon: "RefreshCw",
        };
      case "account_blocked":
        return {
          handler: handleLogoutAndReconnect,
          label: t("whatsappOAuth.connectAnotherButton"),
          icon: "UserPlus",
        };
      case "pending_registration":
        return {
          handler: handleVerifyRegistration,
          label: t("whatsappOAuth.checkStatusButton"),
          icon: "Search",
        };
      default:
        return {
          handler: handleConnectWithPinCheck,
          label: t("whatsappOAuth.reconnectButton"),
          icon: "RefreshCw",
        };
    }
  };

  // Format connected date
  const formatConnectedAt = (dateStr) => {
    if (!dateStr) return null;
    try {
      return new Date(dateStr).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return null;
    }
  };

  // Get error message for misconfigured reason
  const getErrorMessage = (reason) => {
    if (!reason) return null;
    const key = `whatsappOAuth.errors.${reason}`;
    const translated = t(key);
    return translated !== key ? translated : reason;
  };

  const isLoading =
    isConnecting ||
    isLoadingSDK ||
    initOAuthMutation.isPending ||
    completeOAuthMutation.isPending ||
    disconnectMutation.isPending ||
    doctorMutation.isPending;
  const isDownloadingPin = downloadPinMutation.isPending;
  const isVerifying = verifyRegistrationMutation.isPending;

  // Auto-connect on mount when autoConnect is true
  const hasAutoConnected = useRef(false);
  useEffect(() => {
    if (
      autoConnect &&
      !hasAutoConnected.current &&
      agent?.id &&
      isBusinessLogin &&
      !isConfigured
    ) {
      hasAutoConnected.current = true;
      handleConnectWithPinCheck();
    }
  }, [
    autoConnect,
    agent?.id,
    isBusinessLogin,
    isConfigured,
    handleConnectWithPinCheck,
  ]);

  return (
    <div className="space-y-4">
      {/* Auth Type Selector */}
      <div>
        <label className="mb-2 block text-sm font-medium text-gray-300">
          {t("whatsappOAuth.authTypeTitle")}
        </label>
        <div className="space-y-2">
          {/* Business Login Option (Recommended) */}
          <label
            className={`flex cursor-pointer items-start rounded-lg border p-3 transition-colors ${
              authType === AUTH_TYPE_BUSINESS_LOGIN
                ? "border-blue-500 bg-blue-900/20"
                : "border-gray-700 bg-gray-800 hover:border-gray-600"
            } ${readOnly ? "cursor-not-allowed opacity-50" : ""}`}
          >
            <input
              type="radio"
              name="authType"
              value={AUTH_TYPE_BUSINESS_LOGIN}
              checked={authType === AUTH_TYPE_BUSINESS_LOGIN}
              onChange={() => handleAuthTypeSelect(AUTH_TYPE_BUSINESS_LOGIN)}
              disabled={readOnly || isLoading}
              className="mt-0.5 h-4 w-4 border-gray-600 bg-gray-700 text-blue-500 focus:ring-blue-500"
            />
            <div className="ml-3 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-white">
                  {t("whatsappOAuth.authTypeBusinessLogin")}
                </span>
                <span className="rounded bg-green-900/30 px-1.5 py-0.5 text-xs text-green-400">
                  {t("whatsappOAuth.authTypeBusinessLoginDesc")
                    .split("(")[1]
                    ?.replace(")", "") || "recommended"}
                </span>
              </div>
              <span className="block text-xs text-gray-400">
                {t("whatsappOAuth.authTypeBusinessLoginDesc").split("(")[0]}
              </span>
            </div>
          </label>

          {/* Manual Option */}
          <label
            className={`flex cursor-pointer items-start rounded-lg border p-3 transition-colors ${
              authType === AUTH_TYPE_MANUAL
                ? "border-blue-500 bg-blue-900/20"
                : "border-gray-700 bg-gray-800 hover:border-gray-600"
            } ${readOnly ? "cursor-not-allowed opacity-50" : ""}`}
          >
            <input
              type="radio"
              name="authType"
              value={AUTH_TYPE_MANUAL}
              checked={authType === AUTH_TYPE_MANUAL}
              onChange={() => handleAuthTypeSelect(AUTH_TYPE_MANUAL)}
              disabled={readOnly || isLoading}
              className="mt-0.5 h-4 w-4 border-gray-600 bg-gray-700 text-blue-500 focus:ring-blue-500"
            />
            <div className="ml-3">
              <span className="block text-sm font-medium text-white">
                {t("whatsappOAuth.authTypeManual")}
              </span>
              <span className="block text-xs text-gray-400">
                {t("whatsappOAuth.authTypeManualDesc")}
              </span>
            </div>
          </label>
        </div>
      </div>

      {/* Business Login Connection Section */}
      {isBusinessLogin && (
        <div className="rounded-lg border border-gray-700 bg-gray-800/50 p-4">
          {/* Connected State */}
          {connectionStatus === "connected" && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center rounded-full border border-green-500/30 bg-green-900/20 px-2 py-0.5 text-xs font-medium text-green-400">
                    <Icon name="Check" className="mr-1 h-3 w-3" />
                    {t("whatsappOAuth.status.connected")}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setShowDisconnectModal(true)}
                  disabled={readOnly || isLoading}
                  className="text-sm text-red-400 transition-colors hover:text-red-300 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {t("whatsappOAuth.disconnectButton")}
                </button>
              </div>

              {/* Connection Info */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                {(countryCode || phoneNumber) && (
                  <div>
                    <span className="text-gray-400">
                      {t("whatsappOAuth.info.phoneNumber")}:
                    </span>
                    <span className="ml-2 text-white">
                      {countryCode ? `+${countryCode} ` : ""}
                      {phoneNumber || "-"}
                    </span>
                  </div>
                )}
                {connectedAt && (
                  <div>
                    <span className="text-gray-400">
                      {t("whatsappOAuth.info.connectedAt")}:
                    </span>
                    <span className="ml-2 text-white">
                      {formatConnectedAt(connectedAt)}
                    </span>
                  </div>
                )}
              </div>

              {/* Diagnose connection */}
              <div className="flex items-center justify-between rounded-lg border border-gray-700 bg-gray-800 p-3">
                <span className="text-sm text-gray-400">
                  {t("whatsappOAuth.diagnose.message")}
                </span>
                <button
                  type="button"
                  onClick={handleDoctor}
                  disabled={readOnly || isLoading}
                  className="inline-flex items-center gap-1.5 rounded-md bg-gray-700 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-gray-600 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {doctorMutation.isPending ? (
                    <>
                      <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      {t("whatsappOAuth.processing")}
                    </>
                  ) : (
                    <>
                      <Icon name="Stethoscope" className="h-3.5 w-3.5" />
                      {t("whatsappOAuth.diagnose.button")}
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Error State */}
          {connectionStatus === "error" &&
            misconfiguredReason &&
            (() => {
              const errorAction = getErrorAction(misconfiguredReason);
              return (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center rounded-full border border-red-500/30 bg-red-900/20 px-2 py-0.5 text-xs font-medium text-red-400">
                      <Icon name="AlertCircle" className="mr-1 h-3 w-3" />
                      {t("whatsappOAuth.status.error")}
                    </span>
                  </div>
                  <p className="text-sm text-red-300">
                    {getErrorMessage(misconfiguredReason)}
                  </p>

                  {!errorAction.messageOnly && (
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={errorAction.handler}
                        disabled={readOnly || isLoading}
                        className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {isLoading ? (
                          <>
                            <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                            {t("whatsappOAuth.processing")}
                          </>
                        ) : (
                          <>
                            <Icon
                              name={errorAction.icon}
                              className="mr-2 h-4 w-4"
                            />
                            {errorAction.label}
                          </>
                        )}
                      </button>
                      {errorAction.showDisconnect && (
                        <button
                          type="button"
                          onClick={() => setShowDisconnectModal(true)}
                          disabled={readOnly || isLoading}
                          className="inline-flex items-center justify-center rounded-lg border border-red-500/30 bg-red-900/20 px-4 py-2 text-sm font-medium text-red-400 transition-colors hover:bg-red-900/40 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <Icon name="LogOut" className="mr-2 h-4 w-4" />
                          {t("whatsappOAuth.disconnectButton")}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}

          {/* Not Connected State */}
          {connectionStatus === "notConnected" && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center rounded-full border border-gray-500/30 bg-gray-700/50 px-2 py-0.5 text-xs font-medium text-gray-400">
                  {t("whatsappOAuth.status.notConnected")}
                </span>
              </div>
              <button
                type="button"
                onClick={handleConnectWithPinCheck}
                disabled={readOnly || isLoading}
                className="inline-flex w-full items-center justify-center rounded-lg bg-[#1877F2] px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[#166FE5] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isLoading ? (
                  <>
                    <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    {t("whatsappOAuth.connecting")}
                  </>
                ) : (
                  <>
                    <Icon name="MessageCircle" className="mr-2 h-4 w-4" />
                    {t("whatsappOAuth.connectButton")}
                  </>
                )}
              </button>
            </div>
          )}

          {/* PIN Info & Download */}
          {pin && providerSettings.registration_status != null && (
            <div className="mt-3 flex items-center justify-between rounded-lg border border-gray-700 bg-gray-800 p-3">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-400">
                  {t("whatsappOAuth.info.pin")}:
                </span>
                <span className="text-sm font-medium text-white">{pin}</span>
                {!pinDownloadedAt && (
                  <span title={t("whatsappOAuth.info.pinNotDownloaded")}>
                    <Icon
                      name="AlertTriangle"
                      className="h-4 w-4 text-yellow-500"
                    />
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={handleDownloadPin}
                disabled={readOnly || isDownloadingPin}
                className="inline-flex items-center gap-1.5 rounded-md bg-gray-700 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-gray-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isDownloadingPin ? (
                  <>
                    <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    {t("whatsappOAuth.info.downloadingPin")}
                  </>
                ) : (
                  <>
                    <Icon name="Download" className="h-3.5 w-3.5" />
                    {t("whatsappOAuth.info.downloadPin")}
                  </>
                )}
              </button>
            </div>
          )}

          {/* Verify Registration */}
          {registrationStatus != null &&
            registrationStatus.toUpperCase() !== "CONNECTED" && (
              <button
                type="button"
                onClick={handleVerifyRegistration}
                disabled={readOnly || isVerifying}
                className="mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-blue-500/30 bg-blue-900/20 px-4 py-2 text-sm font-medium text-blue-400 transition-colors hover:bg-blue-900/40 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isVerifying ? (
                  <>
                    <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-blue-400 border-t-transparent" />
                    {t("whatsappOAuth.info.verifyingRegistration")}
                  </>
                ) : (
                  <>
                    <Icon name="RefreshCw" className="h-3.5 w-3.5" />
                    {["FLAGGED", "RESTRICTED"].includes(
                      registrationStatus.toUpperCase(),
                    )
                      ? t("whatsappOAuth.info.refreshStatus")
                      : t("whatsappOAuth.info.verifyRegistration")}
                  </>
                )}
              </button>
            )}
        </div>
      )}

      {/* Disconnect Confirmation Modal */}
      <WhatsAppDisconnectModal
        isOpen={showDisconnectModal}
        onClose={() => setShowDisconnectModal(false)}
        onConfirm={handleDisconnect}
        isLoading={disconnectMutation.isPending}
      />

      {/* PIN Confirmation Modal */}
      {showPinConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="mx-4 max-w-md rounded-lg border border-gray-700 bg-gray-900 p-6">
            <h3 className="mb-2 text-lg font-semibold text-white">
              {t("whatsappOAuth.pinConfirmModal.title")}
            </h3>
            <div className="mb-4 rounded-lg border border-yellow-800 bg-yellow-900/20 p-3">
              <div className="flex items-start space-x-3">
                <Icon
                  name="AlertTriangle"
                  className="mt-0.5 h-5 w-5 flex-shrink-0 text-yellow-500"
                />
                <p className="text-sm text-yellow-300">
                  {t("whatsappOAuth.pinConfirmModal.message")}
                </p>
              </div>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setShowPinConfirmModal(false);
                  handleConnect();
                }}
                className="rounded-lg border border-gray-600 px-4 py-2 text-sm font-medium text-gray-300 transition-colors hover:bg-gray-800"
              >
                {t("whatsappOAuth.pinConfirmModal.continue")}
              </button>
              <button
                type="button"
                onClick={() => setShowPinConfirmModal(false)}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
              >
                {t("whatsappOAuth.pinConfirmModal.changePin")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Auth Type Change Confirmation Modal */}
      {showAuthTypeChangeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="mx-4 max-w-md rounded-lg border border-gray-700 bg-gray-900 p-6">
            <h3 className="mb-2 text-lg font-semibold text-white">
              {t("whatsappOAuth.changeAuthTypeModal.title")}
            </h3>
            <div className="mb-4 rounded-lg border border-yellow-800 bg-yellow-900/20 p-3">
              <div className="flex items-start space-x-3">
                <Icon
                  name="AlertTriangle"
                  className="mt-0.5 h-5 w-5 flex-shrink-0 text-yellow-500"
                />
                <p className="text-sm text-yellow-300">
                  {t("whatsappOAuth.changeAuthTypeModal.message")}
                </p>
              </div>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setShowAuthTypeChangeModal(false);
                  setPendingAuthType(null);
                }}
                className="rounded-lg border border-gray-600 px-4 py-2 text-sm font-medium text-gray-300 transition-colors hover:bg-gray-800"
              >
                {t("whatsappOAuth.changeAuthTypeModal.cancel")}
              </button>
              <button
                type="button"
                onClick={handleAuthTypeChangeConfirm}
                className="rounded-lg bg-yellow-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-yellow-700"
              >
                {t("whatsappOAuth.changeAuthTypeModal.confirm")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WhatsAppAuthSection;
